/**
 * Talent Acquisition → Actividad: quién le preguntó qué al Agente TA Metrics,
 * qué respondió, con qué herramientas y qué marcó el validador (audit RNF-05).
 * Solo admins de la sección: las preguntas de otras personas son información
 * sensible de TA. Paginado hacia atrás con "Cargar más".
 */

import { currentLocale } from '@/lib/locale';
import { StatCard } from '@/components/ui/stat-card';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, RefreshCw, Users, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { BrainMarkdown } from '@/components/brain/BrainMarkdown';
import { useTaActivity } from '@/hooks/useTalentAcquisition';
import { cn } from '@/lib/utils';
import type { TaActivityItem } from '../../../../shared/types/ta-metrics';
import { activityStats, visibleIssues } from './ta-activity-stats';

const fmt = (iso: string) => (iso ? new Date(iso).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' }) : '');


function ActivityRow({ item }: { item: TaActivityItem }) {
  const { t } = useTranslation();
  const k = 'talent_acquisition.activity';
  const [open, setOpen] = useState(false);
  const issues = visibleIssues(item.validation.issues);
  return (
    <div className="border-b last:border-b-0">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/60">
        {open ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{fmt(item.at)}</span>
            <span>·</span>
            <span className="font-medium text-foreground">{item.user}</span>
            {item.clasificacion && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                {t(`${k}.tipo_${item.clasificacion.tipo}`)}
              </Badge>
            )}
            <span>{(item.latencyMs / 1000).toFixed(1)} s</span>
            {item.validation.regenerated && <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{t(`${k}.regenerated`)}</Badge>}
          </div>
          <p className={cn('mt-0.5 text-sm', !open && 'truncate')}>{item.question}</p>
        </div>
        <div className="shrink-0 pt-0.5" title={issues.length ? issues.join(' · ') : t(`${k}.ok`)}>
          {issues.length ? <AlertTriangle className="h-4 w-4 text-amber-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
        </div>
      </button>
      {open && (
        <div className="space-y-3 border-t bg-muted/30 px-3 py-3 pl-10">
          <div className="prose prose-sm max-w-none dark:prose-invert [&_table]:text-xs">
            <BrainMarkdown content={item.reply} />
          </div>
          {issues.length > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{t(`${k}.flagged`, { issues: issues.join(' · ') })}</span>
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>{t(`${k}.tools`)}:</span>
            {item.toolCalls.length === 0 && <span>—</span>}
            {item.toolCalls.map((c, i) => (
              <code key={i} className="rounded bg-muted px-1.5 py-0.5 text-[11px]" title={JSON.stringify(c.input)}>{c.name}</code>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {item.dataset ? t(`${k}.dataset_line`, { rows: item.dataset.filas, at: fmt(item.dataset.downloadedAt), today: item.dataset.hoy }) : t(`${k}.no_dataset`)}
            {item.tokens ? ` · ${t(`${k}.tokens`, { input: item.tokens.input, output: item.tokens.output })}` : ''}
          </p>
        </div>
      )}
    </div>
  );
}

export function TaActivityTab() {
  const { t } = useTranslation();
  const k = 'talent_acquisition.activity';
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } = useTaActivity();
  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const stats = activityStats(items);

  if (isLoading) return <div className="flex items-center justify-center py-24"><WinclapLoader size={40} /></div>;
  if (isError) {
    return (
      <div className="flex flex-wrap items-center gap-3 py-8">
        <p className="flex items-center gap-1.5 text-sm text-destructive"><AlertTriangle className="h-4 w-4" />{t(`${k}.load_error`)}</p>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => refetch()}><RefreshCw className="h-3.5 w-3.5" /> {t(`${k}.retry`)}</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t(`${k}.intro`)}</p>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} /> {t(`${k}.refresh`)}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard layout="left" tone="default" label={t(`${k}.kpi_questions`)} value={stats.n} icon={<Zap className="h-4 w-4" />} />
        <StatCard layout="left" tone="default" label={t(`${k}.kpi_users`)} value={stats.users} icon={<Users className="h-4 w-4" />} />
        <StatCard layout="left" tone="default" label={t(`${k}.kpi_latency`)} value={`${(stats.medianMs / 1000).toFixed(1)} s`} icon={<RefreshCw className="h-4 w-4" />} />
        <StatCard layout="left" tone="default" label={t(`${k}.kpi_flagged`)} value={stats.flagged} icon={<AlertTriangle className="h-4 w-4" />} />
      </div>

      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t(`${k}.empty`)}</p>
          ) : (
            items.map((item) => <ActivityRow key={item.id} item={item} />)
          )}
        </CardContent>
      </Card>

      {hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? t(`${k}.loading_more`) : t(`${k}.load_more`)}
          </Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground">{t(`${k}.footer`, { count: items.length })}</p>
    </div>
  );
}
